/**
 * Which of the caller's companies this tab is acting as (`KLQ-20260819-007` §3, §6).
 *
 * **The URL is the source of truth, not a store.** Workspace-scoped screens live under
 * `/w/$workspaceId/…`, so each tab carries its own answer, every such screen is
 * deep-linkable, and the wrong-company case is visible in the address bar. The rejected
 * alternative — one workspace in `localStorage` applied to every request — is less work and
 * wrong in a way that only shows up in production: two tabs share one `localStorage`, so a
 * person reviewing studio A in one and switching to studio B in another has a first tab that
 * writes into B while still displaying A. Ambient state deciding which company a write lands
 * in is exactly the class of mistake retro rule 5 names.
 *
 * Screens that are **not** workspace-scoped still need an answer — B1 spans memberships by
 * design and does not move under `/w/`, but its ownership chips are relative to a company and
 * `POST /projects` has to file the new project under one. For those the server's own choice
 * is used: `me.active_workspace_id`, which is what `active_workspace` resolved for that very
 * request. Echoing it rather than guessing is what stops the chrome from naming one company
 * while a write lands in another.
 */

import { useParams } from '@tanstack/react-router';

import type { Me } from './queries';
import { useMe } from './queries';

/** One workspace the caller is a member of, as `GET /auth/me` reports it. */
export type WorkspaceMembership = Me['workspaces'][number];

/**
 * A workspace that has been through B0 — and therefore has a name somebody chose.
 *
 * **`MCK-20260812-009` §E, as a type.** An unclaimed workspace's name and slug are generated
 * from the signup address, so the server sends them as `null` until the claim. That makes
 * "may I render this workspace?" a question TypeScript asks at every site instead of a rule
 * one component remembers: a `WorkspaceMembership` cannot be handed to anything that draws a
 * name, and narrowing it to a `Company` is exactly the §E check.
 */
export type Company = WorkspaceMembership & { readonly name: string; readonly slug: string };

/**
 * Whether this workspace is a company the caller can be shown.
 *
 * `claimed` alone would be enough for the runtime answer — the server nulls the pair on
 * exactly that condition — but the null checks are what narrow the type, and asserting the
 * server's invariant here rather than trusting it is what keeps a payload change from
 * becoming a blank menu entry.
 */
export function isCompany(workspace: WorkspaceMembership): workspace is Company {
  return workspace.claimed && workspace.name !== null && workspace.slug !== null;
}

/**
 * The caller's companies, in the order the server sent them, for a menu or a heading.
 *
 * Not every workspace: somebody who signed up and has not claimed holds one that is not a
 * company yet, and §E says it is never presented. Filtering here rather than in the menu is
 * what makes `companies.length > 1` mean *works for two firms* — the question the switcher is
 * actually asking — rather than *has two rows in the payload*.
 */
export function useCompanies(): readonly Company[] {
  const { data: me } = useMe();
  if (me === undefined) return [];
  return me.workspaces.filter(isCompany);
}

/**
 * The active workspace id, or `null` while the session is still loading.
 *
 * `strict: false` because this is read from screens both inside and outside the `/w/` branch
 * — the router's typed `useParams` would refuse the ones outside, and the honest answer for
 * them is "there is no parameter here", not a type error.
 *
 * A route parameter naming a workspace the caller is **not** a member of is discarded rather
 * than honoured. The server would answer `403` either way; discarding it means a stale
 * bookmark from a studio somebody has left degrades to their own company instead of turning
 * every request on the screen into a refusal.
 */
export function useActiveWorkspaceId(): string | null {
  const params: Record<string, string | undefined> = useParams({ strict: false });
  const { data: me } = useMe();
  if (me === undefined) return null;

  const fromUrl = params.workspaceId;
  if (fromUrl !== undefined && me.workspace_ids.includes(fromUrl)) return fromUrl;

  return me.active_workspace_id ?? me.home_workspace_id;
}

/** The active workspace as a row, for a heading or a menu. `null` until the session lands. */
export function useActiveWorkspace(): WorkspaceMembership | null {
  const { data: me } = useMe();
  const activeId = useActiveWorkspaceId();
  if (me === undefined || activeId === null) return null;
  return me.workspaces.find((workspace) => workspace.id === activeId) ?? null;
}

/**
 * Whether the company this tab is acting as has been through B0.
 *
 * **`MCK-20260812-009` §E, re-read for several memberships.** The rule is *"a session whose
 * home workspace is unclaimed sees no workspace chrome"*, written when home was the only
 * workspace a person could have. Keyed off the *active* workspace it means the same thing for
 * everybody with one company — active is home — and the right thing for somebody with two: an
 * external collaborator whose own home is unclaimed, later hired by a studio, gets that
 * studio's chrome while acting as it, and still gets none while acting as their own.
 */
export function useActiveWorkspaceIsClaimed(): boolean {
  const { data: me } = useMe();
  const active = useActiveWorkspace();
  if (me === undefined) return false;
  return active?.claimed ?? me.home_workspace_claimed;
}
